import { useState, useEffect } from 'react';
import axios from 'axios';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../components/ui/table';
import { ClipboardList, CheckCircle2, Clock } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL || (window.location.hostname === "localhost" ? "http://localhost:8000" : window.location.origin);

export default function CashSessions() {
  const [sessions, setSessions] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchSessions();
  }, []);

  const fetchSessions = async () => {
    try {
      const { data } = await axios.get(`${API_URL}/api/cash-sessions`, { withCredentials: true });
      setSessions(data);
    } catch (error) {
      console.error('Error fetching sessions:', error);
    } finally {
      setLoading(false);
    }
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('es-CL', { style: 'currency', currency: 'CLP' }).format(amount || 0);
  };

  const formatDateTime = (isoString) => {
    if (!isoString) return '-';
    return new Date(isoString).toLocaleString('es-CL', {
      dateStyle: 'short',
      timeStyle: 'short'
    });
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-amber-600"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="cash-sessions-page">
      {/* Header */}
      <div>
        <h1 className="text-3xl font-extrabold text-stone-900 tracking-tight font-heading">Arqueos de Caja</h1>
        <p className="text-stone-500 mt-1">Historial de aperturas y cierres</p>
      </div>

      {/* Summary Cards */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
        <Card className="bg-white border-stone-200 rounded-2xl">
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium text-stone-500">Total Sesiones</CardTitle>
          </CardHeader>
          <CardContent>
            <p className="text-2xl font-bold text-stone-900">{sessions.length}</p>
          </CardContent>
        </Card>
        <Card className="bg-white border-stone-200 rounded-2xl">
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium text-stone-500">Cajas Abiertas</CardTitle>
          </CardHeader>
          <CardContent>
            <p className="text-2xl font-bold text-amber-600">
              {sessions.filter(s => s.status === 'open').length}
            </p>
          </CardContent>
        </Card>
        <Card className="bg-white border-stone-200 rounded-2xl">
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium text-stone-500">Diferencias Negativas</CardTitle>
          </CardHeader>
          <CardContent>
            <p className="text-2xl font-bold text-red-500">
              {sessions.filter(s => s.difference && s.difference < 0).length}
            </p>
          </CardContent>
        </Card>
      </div>

      {/* Table */}
      <div className="bg-white rounded-2xl border border-stone-200 overflow-hidden">
        <Table>
          <TableHeader>
            <TableRow className="bg-stone-50">
              <TableHead className="font-semibold">Estado</TableHead>
              <TableHead className="font-semibold">Cajero</TableHead>
              <TableHead className="font-semibold">Apertura</TableHead>
              <TableHead className="font-semibold">Cierre</TableHead>
              <TableHead className="font-semibold text-right">Inicial</TableHead>
              <TableHead className="font-semibold text-right">Ventas</TableHead>
              <TableHead className="font-semibold text-right">Final</TableHead>
              <TableHead className="font-semibold text-right">Diferencia</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {sessions.length === 0 ? (
              <TableRow>
                <TableCell colSpan={8} className="text-center py-12 text-stone-500">
                  <ClipboardList className="w-12 h-12 mx-auto mb-3 text-stone-300" />
                  No hay registros de arqueos
                </TableCell>
              </TableRow>
            ) : (
              sessions.map((session) => (
                <TableRow key={session.id} className="hover:bg-stone-50/50" data-testid={`session-row-${session.id}`}>
                  <TableCell>
                    {session.status === 'open' ? (
                      <span className="inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium bg-amber-50 text-amber-700">
                        <Clock className="w-3 h-3" />
                        Abierta
                      </span>
                    ) : (
                      <span className="inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium bg-emerald-50 text-emerald-700">
                        <CheckCircle2 className="w-3 h-3" />
                        Cerrada
                      </span>
                    )}
                  </TableCell>
                  <TableCell className="font-medium text-stone-900">{session.cashier_name}</TableCell>
                  <TableCell className="text-stone-600">{formatDateTime(session.opened_at)}</TableCell>
                  <TableCell className="text-stone-600">{formatDateTime(session.closed_at)}</TableCell>
                  <TableCell className="text-right text-stone-900">{formatCurrency(session.initial_cash)}</TableCell>
                  <TableCell className="text-right">
                    <span className="text-stone-900">{formatCurrency(session.total_sales)}</span>
                    <span className="text-xs text-stone-500 ml-1">({session.sales_count})</span>
                  </TableCell>
                  <TableCell className="text-right text-stone-900">
                    {session.final_cash !== null ? formatCurrency(session.final_cash) : '-'}
                  </TableCell>
                  <TableCell className="text-right">
                    {session.difference !== null ? (
                      <span className={`font-semibold ${session.difference >= 0 ? 'text-emerald-600' : 'text-red-600'}`}>
                        {session.difference >= 0 ? '+' : ''}{formatCurrency(session.difference)}
                      </span>
                    ) : '-'}
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </div>
    </div>
  );
}
